/**
 * Small localStorage-backed MRU (Most Recently Used) tracker for insights
 * the current user has visited. Feeds the "Recently viewed" section of the
 * drill-through picker so users can one-tap-link to insights they've been
 * working with.
 *
 * Pinning: users can pin any recent insight to keep it available regardless
 * of MRU eviction. Pinned items sort above unpinned ones and are never
 * dropped by the MRU limit. Persistence stays client-local (localStorage)
 * to match the rest of the recent-insights behavior.
 */

const KEY = 'inzara.recentInsights';
const PIN_KEY = 'inzara.pinnedInsights';
const MAX = 8;

function readList(key) {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return [];
    const list = JSON.parse(raw);
    return Array.isArray(list) ? list : [];
  } catch (_) { return []; }
}

function writeList(key, list) {
  try { window.localStorage.setItem(key, JSON.stringify(list)); }
  catch (_) { /* quota / private mode — ignore */ }
}

export function recordInsightView({ id, name, kind }) {
  if (!id) return;
  const list = readList(KEY);
  const filtered = list.filter(x => x.id !== id);
  filtered.unshift({ id, name: name || '(untitled)', kind: kind || 'Report', at: Date.now() });
  writeList(KEY, filtered.slice(0, MAX));
}

export function getPinnedIds() {
  return readList(PIN_KEY).map(p => p.id).filter(Boolean);
}

export function isInsightPinned(id) {
  if (!id) return false;
  return getPinnedIds().includes(id);
}

export function pinInsight({ id, name, kind }) {
  if (!id) return;
  const pins = readList(PIN_KEY).filter(p => p.id !== id);
  pins.unshift({ id, name: name || '(untitled)', kind: kind || 'Report', at: Date.now() });
  writeList(PIN_KEY, pins);
}

export function unpinInsight(id) {
  if (!id) return;
  const pins = readList(PIN_KEY).filter(p => p.id !== id);
  writeList(PIN_KEY, pins);
}

export function togglePinInsight(entry) {
  if (!entry || !entry.id) return false;
  if (isInsightPinned(entry.id)) {
    unpinInsight(entry.id);
    return false;
  }
  pinInsight(entry);
  return true;
}

/**
 * Return recent insights, with pinned entries surfaced first (in pin order),
 * followed by unpinned MRU entries. Pins are annotated with `pinned: true`.
 * The `limit` bounds the unpinned tail so pinned entries never fall out.
 */
export function getRecentInsights(limit = 3) {
  const pins = readList(PIN_KEY);
  const pinIds = new Set(pins.map(p => p.id));
  const recents = readList(KEY);
  const unpinned = recents.filter(r => !pinIds.has(r.id)).slice(0, limit);
  const merged = [
    ...pins.map(p => ({ ...p, pinned: true })),
    ...unpinned.map(r => ({ ...r, pinned: false })),
  ];
  return merged;
}

export function clearRecentInsights() {
  try { window.localStorage.removeItem(KEY); } catch (_) { /* ignore */ }
}
